/**
 * Import Content
 *
 * Launch landing: migrate ChatGPT / Claude / Gemini context into
 * Losi Context Bank, then keep using it across models via MCP / API / OAuth.
 */

'use client'

import Link from 'next/link'
import {
  ArrowRight,
  Brain,
  FolderInput,
  HardDriveUpload,
  MessageSquare
} from 'lucide-react'
import {
  IconBrandGoogle,
  IconBrandOpenai,
  IconDatabase,
  IconEyeCheck,
  IconHistory,
  IconKey,
  IconMessage,
  IconMessages,
  IconPlugConnected,
  IconSparkles,
  IconUpload
} from '@tabler/icons-react'
import { motion } from 'motion/react'

import { ImportContextFlow } from '@/components/marketing/import-context-flow'
import { ImportLandingCta } from '@/components/marketing/import-landing-cta'
import { CultTextureButton } from '@/components/marketing/cult-ui-components'
import { MarketingNumberTicker } from '@/components/marketing/marketing-number-ticker'
import { MarketingReveal } from '@/components/marketing/marketing-motion'
import { MarketingShineBorder } from '@/components/marketing/marketing-shine-border'
import {
  MarketingContainer,
  MarketingEyebrow,
  MarketingPage,
  MarketingRailIcon
} from '@/components/marketing/marketing-primitives'

const providers = [
  {
    provider: 'chatgpt' as const,
    name: 'ChatGPT',
    summary: 'Use a portable memory export or a full account export ZIP.',
    icon: IconBrandOpenai
  },
  {
    provider: 'claude' as const,
    name: 'Claude',
    summary: 'Bring over saved memory context and exported conversation history.',
    icon: IconMessage
  },
  {
    provider: 'gemini' as const,
    name: 'Gemini',
    summary: 'Paste structured summaries or upload exported chat archives.',
    icon: IconBrandGoogle
  }
]

const bankSurfaces = [
  'Memories',
  'Spaces',
  'Skills',
  'Soul',
  'Knowledge graph',
  'Nexus CRM',
  'MCP + API'
]

const landsInBank = [
  {
    title: 'Memories',
    description:
      'Preferences, facts, and evidence land as reviewable Context Bank memory — not anonymous paste.',
    icon: IconSparkles
  },
  {
    title: 'Conversations',
    description:
      'Chats become real Losi threads with titles, roles, timestamps, and source provenance attached.',
    icon: IconMessages
  },
  {
    title: 'Provenance',
    description:
      'You keep where each item came from, so migrated context stays auditable after the switch.',
    icon: IconHistory
  }
]

const transferSteps = [
  {
    label: 'Export',
    text: 'Upload or paste an export from ChatGPT, Claude, or Gemini.',
    icon: IconUpload
  },
  {
    label: 'Preview',
    text: 'Review memories and conversations before anything is written.',
    icon: IconEyeCheck
  },
  {
    label: 'Bank it',
    text: 'Select what belongs, then keep it in Context Bank for Losi and connected agents.',
    icon: IconDatabase
  }
]

const afterImport = [
  {
    title: 'Use it in Losi',
    description: 'Chat, Spaces, and Nexus read the same bank you just filled.'
  },
  {
    title: 'Sync to other AIs',
    description: 'Connect Claude, Cursor, Codex, and more over MCP or REST.'
  },
  {
    title: 'API key or OAuth',
    description: 'Servers use workspace keys. Interactive apps open a grant page for exact scopes.'
  }
]

export function ImportContent() {
  return (
    <MarketingPage>
      <MarketingContainer>
        {/* Hero — Context Bank first */}
        <section className="relative grid gap-10 py-4 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-14">
          <div className="flex max-w-[40rem] flex-col gap-5">
            <MarketingEyebrow>Import · Context Bank</MarketingEyebrow>
            <h1 className="font-display max-w-[15ch] text-4xl font-medium leading-[0.96] tracking-[-0.02em] text-slate-950 sm:text-5xl lg:text-[4.75rem] dark:text-slate-50">
              Bring your AI history into Context Bank.
            </h1>
            <p className="max-w-[44ch] text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
              Move memories and conversations from ChatGPT, Claude, and Gemini into Losi Context
              Bank. Preview first, keep provenance, then use that context in Losi — or sync it to
              other models over MCP and API.
            </p>
            <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:flex-wrap">
              <ImportLandingCta
                importType="memory"
                className="rounded-full px-6"
                surface="hero_memory"
              >
                <Brain className="size-4" />
                <span>Import memories</span>
              </ImportLandingCta>
              <ImportLandingCta
                importType="conversations"
                variant="outline"
                className="rounded-full px-6"
                surface="hero_conversations"
              >
                <MessageSquare className="size-4" />
                <span>Import conversations</span>
              </ImportLandingCta>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Free to start · Preview before save · Docs for MCP + OAuth
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative min-h-[260px]"
          >
            <ImportContextFlow />
            <p className="mt-6 max-w-[36ch] text-sm leading-7 text-slate-600 dark:text-slate-300">
              Exported context does not stop at chat history. It becomes durable bank memory you can
              search, share with Spaces, and expose to agents.
            </p>
          </motion.div>
        </section>

        {/* Context Bank — launch break */}
        <section className="relative mt-12 overflow-visible py-12 lg:mt-16 lg:py-16">
          <MarketingShineBorder>
            <div className="px-6 py-12 text-slate-50 sm:px-8 sm:py-14 lg:px-12 lg:py-16">
              <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
                <div className="flex max-w-[40rem] flex-col gap-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                    Losi Context Bank
                  </p>
                  <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-white sm:text-5xl">
                    One bank. Switch models freely. Context stays.
                  </h2>
                  <p className="text-base leading-8 text-slate-300 sm:text-lg">
                    Context Bank is the launch surface for portable memory: personal and workspace
                    memories, Space knowledge, skills, soul, and the graph — available inside Losi and
                    to tools you connect with a workspace API key or OAuth grant.
                  </p>
                  <p className="text-sm leading-7 text-slate-400">
                    {bankSurfaces.join(' · ')}
                  </p>
                  <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <CultTextureButton asChild>
                      <Link href="/articles/use-context-bank-across-tools">
                        Read the Context Bank guide
                        <ArrowRight className="ml-2 size-4" />
                      </Link>
                    </CultTextureButton>
                    <Link
                      href="/login?register=true"
                      className="inline-flex items-center text-sm font-medium text-slate-200 transition-colors hover:text-white"
                    >
                      Start free
                      <ArrowRight className="ml-1.5 size-3.5" />
                    </Link>
                  </div>
                </div>

                <div className="flex flex-col justify-center gap-8 border-t border-white/10 pt-8 lg:border-t-0 lg:border-l lg:pl-10 lg:pt-0">
                  <div>
                    <p className="font-display text-5xl font-medium tracking-tight text-white">
                      <MarketingNumberTicker value={bankSurfaces.length} />
                    </p>
                    <p className="mt-2 text-sm leading-7 text-slate-400">
                      surfaces in the bank your import can feed
                    </p>
                  </div>
                  <ul className="space-y-5">
                    {afterImport.map(item => (
                      <li key={item.title}>
                        <p className="text-base font-semibold text-white">{item.title}</p>
                        <p className="mt-1 text-sm leading-7 text-slate-300">{item.description}</p>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-3 text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <IconKey className="size-3.5" stroke={1.5} />
                      API key
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <IconPlugConnected className="size-3.5" stroke={1.5} />
                      OAuth
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <IconDatabase className="size-3.5" stroke={1.5} />
                      MCP + REST
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </MarketingShineBorder>
        </section>

        {/* What lands */}
        <section className="mt-12 py-8 lg:mt-14 lg:py-12">
          <div className="mx-auto max-w-[46rem] text-center">
            <MarketingEyebrow>What lands in the bank</MarketingEyebrow>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              Memories, chats, and provenance — attached, not dumped.
            </h2>
          </div>
          <div className="mt-12 grid gap-0 border-y border-slate-200/80 dark:border-white/10 sm:grid-cols-3">
            {landsInBank.map((item, index) => (
              <MarketingReveal key={item.title} delay={0.06 * index} distance={16} blurPx={6}>
                <article
                  className={`flex h-full flex-col gap-3 px-5 py-8 ${
                    index > 0
                      ? 'border-t border-slate-200/80 dark:border-white/10 sm:border-t-0 sm:border-l'
                      : ''
                  }`}
                >
                  <MarketingRailIcon icon={item.icon} />
                  <h3 className="text-xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                    {item.description}
                  </p>
                </article>
              </MarketingReveal>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="mt-8 py-8 lg:mt-12 lg:py-12">
          <div className="mx-auto max-w-[46rem]">
            <MarketingEyebrow>How it works</MarketingEyebrow>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              Export. Preview. Bank it.
            </h2>
          </div>
          <ol className="mt-10 grid gap-0 border-y border-slate-200/80 dark:border-white/10 lg:grid-cols-3">
            {transferSteps.map((step, index) => (
              <li
                key={step.label}
                className={`relative flex flex-col gap-3 px-5 py-8 ${
                  index > 0
                    ? 'border-t border-slate-200/80 dark:border-white/10 lg:border-t-0 lg:border-l'
                    : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <MarketingRailIcon icon={step.icon} />
                  <span className="text-[11px] font-semibold tabular-nums tracking-[0.18em] text-slate-400">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-lg font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                  {step.label}
                </h3>
                <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Choose a source */}
        <section className="mt-8 py-8 lg:mt-12 lg:py-12">
          <div className="mx-auto max-w-[46rem]">
            <MarketingEyebrow>Choose a source</MarketingEyebrow>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              Start from the assistant you are leaving.
            </h2>
          </div>
          <div className="mx-auto mt-10 max-w-[52rem] space-y-0">
            {providers.map(({ provider, name, summary, icon: Icon }) => (
              <article
                key={provider}
                className="grid gap-5 border-t border-slate-200/80 py-8 first:border-t-0 first:pt-0 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] sm:gap-8 dark:border-white/10"
              >
                <div className="flex items-center gap-3 sm:flex-col sm:items-start sm:gap-4">
                  <span className="flex size-12 items-center justify-center rounded-2xl bg-[rgb(236,240,247)] text-[rgb(60,82,110)] dark:bg-sky-500/15 dark:text-sky-200">
                    <Icon className="size-6" stroke={1.5} />
                  </span>
                  <p className="text-lg font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                    {name}
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                    {name} → Context Bank
                  </h3>
                  <p className="mt-2 max-w-[42ch] text-sm leading-7 text-slate-600 dark:text-slate-300">
                    {summary}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <ImportLandingCta
                      importType="memory"
                      provider={provider}
                      size="sm"
                      className="rounded-full"
                      surface={`${provider}_memory`}
                    >
                      <Brain className="size-4" />
                      <span>Import {name} memory</span>
                    </ImportLandingCta>
                    <ImportLandingCta
                      importType="conversations"
                      provider={provider}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      surface={`${provider}_conversations`}
                    >
                      <MessageSquare className="size-4" />
                      <span>Import {name} chats</span>
                    </ImportLandingCta>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-8 py-10 lg:mt-12 lg:py-14">
          <div className="flex max-w-[46rem] flex-col gap-4">
            <MarketingEyebrow>Ready for launch</MarketingEyebrow>
            <h2 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              Import once. Keep building from Context Bank.
            </h2>
            <p className="max-w-[44ch] text-base leading-8 text-slate-600 dark:text-slate-300">
              Bring over what matters, keep provenance, then connect other tools to the same bank —
              so changing models does not mean starting over.
            </p>
            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap">
              <ImportLandingCta
                importType="memory"
                className="rounded-full px-6"
                surface="footer_memory"
              >
                <HardDriveUpload className="size-4" />
                <span>Open memory importer</span>
              </ImportLandingCta>
              <ImportLandingCta
                importType="conversations"
                variant="outline"
                className="rounded-full px-6"
                surface="footer_conversations"
              >
                <FolderInput className="size-4" />
                <span>Open conversation importer</span>
                <ArrowRight className="ml-1 size-4" />
              </ImportLandingCta>
              <Link
                href="/articles/use-context-bank-across-tools"
                className="inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-950 dark:text-slate-300 dark:hover:text-white"
              >
                Context Bank guide
                <ArrowRight className="ml-1.5 size-3.5" />
              </Link>
            </div>
          </div>
        </section>
      </MarketingContainer>
    </MarketingPage>
  )
}
